import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import { getEnsOrAddress } from '@/utils/address/getEnsOrAddress'
import { cn } from '@/utils/cn'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAddress } from '@vaultsfyi/common/utils'
import { useForm } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router'
import { useConfig } from 'wagmi'
import { z } from 'zod'
import { Button } from '../common/button'
import { Input } from '../common/input'
import { Label } from '../common/label'

interface Props {
  address?: string
  onSubmitAction?: () => void
}

const addressSchema = z.object({
  address: z.string({ required_error: 'Address cannot be empty' }).superRefine((val, ctx) => {
    if (val.endsWith('.eth')) {
      return
    }

    if (!isAddress(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid wallet address',
      })
    }
  }),
})

export const AddressForm = ({ address, onSubmitAction }: Props) => {
  const { trackEvent } = useMixpanel()
  const navigate = useNavigate()
  const wagmiConfig = useConfig()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: zodResolver(addressSchema),
    defaultValues: { address: address ?? '' },
  })

  const onSubmit = handleSubmit(async (data: { address: string }) => {
    try {
      const { address, method } = await getEnsOrAddress(data.address, wagmiConfig)
      trackEvent('Track wallet', { Method: method })
      navigate(generatePath(routes.dashboard, { address }))
      onSubmitAction?.()
    } catch (error) {
      if (error instanceof Error) {
        setError('address', { message: error.message })
      }
    }
  })

  return (
    <form className="flex w-full flex-col gap-4 md:gap-5" onSubmit={onSubmit}>
      <div className="flex flex-col items-start gap-1">
        <Label className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-foreground text-sm">Enter wallet address or ENS</span>
          <Input
            placeholder="Enter wallet address or ENS..."
            className={cn('h-12 bg-page', errors.address && 'border-destructive hocus:border-destructive')}
            {...register('address')}
          />
        </Label>
        {errors.address && <p className="text-destructive text-sm">{errors.address.message}</p>}
      </div>
      <Button variant="primary" size="lg" className="h-12 w-full" type="submit">
        Track with wallet address
      </Button>
    </form>
  )
}
